import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
} from "@mui/material";
import { Edit, Visibility, Delete } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import auctionService from "../services/auctionService";
import { Item } from "../types/Item";
import { getImageUrl } from "../utils/image";

const AdminLiveAuctions: React.FC = () => {
  const [auctions, setAuctions] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [auctionToDelete, setAuctionToDelete] = useState<Item | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchAuctions = async () => {
    try {
      setLoading(true);
      const items = await auctionService.getAuctions("active");
      setAuctions(items);
      setError(null);
    } catch (err: any) {
      setError("Failed to fetch live auctions. Please try again later.");
      console.error("Error fetching live auctions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuctions();
  }, []);

  const handleDeleteClick = (auction: Item) => {
    setAuctionToDelete(auction);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!auctionToDelete) return;
    setDeleting(true);
    try {
      await auctionService.deleteLiveAuction(auctionToDelete.id);
      setDeleteDialogOpen(false);
      setAuctionToDelete(null);
      setSuccessMessage("Auction deleted successfully!");
      fetchAuctions(); // Refresh the list
    } catch (err: any) {
      setError(err.message || "Failed to delete auction");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAuctionToDelete(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" align="center">
          {error}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button variant="contained" onClick={fetchAuctions}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Live - Adm
      </Typography>
      <Paper sx={{ p: 2, maxWidth: 800, mx: "auto" }}>
        {auctions.length === 0 ? (
          <Typography color="text.secondary" align="center">
            No live auctions found.
          </Typography>
        ) : (
          <List>
            {auctions.map((item, index) => (
              <ListItem key={item.id}>
                {item.images && item.images.length > 0 ? (
                  <img
                    src={getImageUrl(item.images[0])}
                    alt={item.title}
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: "cover",
                      marginRight: 16,
                      borderRadius: 4,
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "/default-auction.png";
                    }}
                  />
                ) : (
                  <img
                    src={getImageUrl()}
                    alt="No auction preview"
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: "cover",
                      marginRight: 16,
                      borderRadius: 4,
                    }}
                  />
                )}
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight="bold">
                      {item.title}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {item.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Status: {item.status}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="view"
                    onClick={() => navigate(`/items/${item.id}`)}
                    color="primary"
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    onClick={() => navigate(`/admin/edit-auction/${item.id}`)}
                    color="secondary"
                    sx={{ ml: 1 }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleDeleteClick(item)}
                    color="error"
                    sx={{ ml: 1 }}
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Auction</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the auction "
            {auctionToDelete?.title}"? This action cannot be undone and will
            permanently remove the auction from the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminLiveAuctions;
